/**
 * 斗兽棋AI逻辑
 * 基于动物等级和位置评估的简单AI
 */

import { JungleGameState, JungleMove, Position, JungleAnimal, Player, Difficulty } from '../types/game';
import { getAllPossibleMoves, checkGameEnd } from './jungle';
import { ANIMAL_RANKS } from '../types/game';

// AI评估权重 - 加强难度
const EVALUATION_WEIGHTS = {
  animalValue: 15,        // 动物价值权重
  positionValue: 4,       // 位置价值权重
  denProximity: 25,       // 巢穴接近度权重
  trapControl: 12,        // 陷阱控制权重
  riverJumpBonus: 15,     // 河跳奖励权重
  captureBonus: 30,       // 吃子奖励权重
  mobility: 6,            // 移动性权重
  protectionBonus: 8,     // 保护弱小动物权重
  threatPenalty: 10,      // 被威胁惩罚权重
};

// 动物价值（基于等级）
const getAnimalValue = (animal: JungleAnimal): number => {
  return ANIMAL_RANKS[animal.type] * 10;
};

// 位置价值评估 - 加强策略性
const getPositionValue = (position: Position, player: Player, gameState: JungleGameState): number => {
  let value = 0;

  // 中心位置更有价值
  const centerDistance = Math.abs(position.col - 3) + Math.abs(position.row - 4);
  value += (10 - centerDistance) * 1.5;

  // 接近对方巢穴的价值 - 大幅增加
  const enemyDen = player === 'black' ? gameState.dens.white : gameState.dens.black;
  const denDistance = Math.abs(position.col - enemyDen.col) + Math.abs(position.row - enemyDen.row);
  value += (14 - denDistance) * 4;

  // 控制陷阱的价值 - 更重要
  const enemyTraps = player === 'black' ? gameState.traps.white : gameState.traps.black;
  const myTraps = player === 'black' ? gameState.traps.black : gameState.traps.white;

  // 控制敌方陷阱
  for (const trap of enemyTraps) {
    const trapDistance = Math.abs(position.col - trap.col) + Math.abs(position.row - trap.row);
    if (trapDistance <= 1) {
      value += 8;
    }
  }

  // 保护己方陷阱
  for (const trap of myTraps) {
    const trapDistance = Math.abs(position.col - trap.col) + Math.abs(position.row - trap.row);
    if (trapDistance <= 2) {
      value += 4;
    }
  }

  // 河流附近的价值（特别是对于能跳河的动物）
  const nearRiver = isNearRiver(position, gameState);
  if (nearRiver) {
    value += 3;
  }

  return value;
};

// 检查是否靠近河流
const isNearRiver = (position: Position, gameState: JungleGameState): boolean => {
  const riverPositions = gameState.riverPositions;
  for (const river of riverPositions) {
    const distance = Math.abs(position.row - river.row) + Math.abs(position.col - river.col);
    if (distance <= 2) {
      return true;
    }
  }
  return false;
};

// 检查动物是否受到威胁
const isUnderThreat = (position: Position, player: Player, gameState: JungleGameState): boolean => {
  const opponent = player === 'black' ? 'white' : 'black';
  const opponentMoves = getAllPossibleMoves(gameState, opponent);

  return opponentMoves.some(move =>
    move.to.row === position.row && move.to.col === position.col
  );
};

// 检查能保护的动物
const getProtectedAnimals = (position: Position, player: Player, gameState: JungleGameState): JungleAnimal[] => {
  const protectedAnimals: JungleAnimal[] = [];
  const opponent = player === 'black' ? 'white' : 'black';

  // 检查相邻位置是否有己方弱小动物
  const adjacentPositions = [
    {row: position.row - 1, col: position.col},
    {row: position.row + 1, col: position.col},
    {row: position.row, col: position.col - 1},
    {row: position.row, col: position.col + 1}
  ];

  for (const adjPos of adjacentPositions) {
    if (adjPos.row >= 0 && adjPos.row < 9 && adjPos.col >= 0 && adjPos.col < 7) {
      const animal = gameState.board[adjPos.row][adjPos.col];
      if (animal && animal.player === player && animal.rank <= 3) { // 保护弱小动物
        const isThreatened = isUnderThreat(adjPos, player, gameState);
        if (isThreatened) {
          protectedAnimals.push(animal);
        }
      }
    }
  }

  return protectedAnimals;
};

// 河跳位置价值
const getRiverJumpValue = (move: JungleMove): number => {
  if (move.isRiverJump) {
    return EVALUATION_WEIGHTS.riverJumpBonus;
  }
  return 0;
};

// 评估当前局面 - 全面加强
const evaluatePosition = (gameState: JungleGameState, player: Player): number => {
  let score = 0;

  // 动物价值评估 - 权重增加
  for (const animal of gameState.animals[player]) {
    score += getAnimalValue(animal) * EVALUATION_WEIGHTS.animalValue / 10;
  }

  for (const animal of gameState.animals[player === 'black' ? 'white' : 'black']) {
    score -= getAnimalValue(animal) * EVALUATION_WEIGHTS.animalValue / 10;
  }

  // 位置价值评估 - 更精细
  for (const animal of gameState.animals[player]) {
    const position = findAnimalPosition(gameState, animal);
    if (position) {
      score += getPositionValue(position, player, gameState);

      // 威胁评估
      const isThreatened = isUnderThreat(position, player, gameState);
      if (isThreatened) {
        score -= EVALUATION_WEIGHTS.threatPenalty * animal.rank;
      }

      // 保护评估
      const protectedAnimals = getProtectedAnimals(position, player, gameState);
      score += protectedAnimals.length * EVALUATION_WEIGHTS.protectionBonus;
    }
  }

  // 被吃掉的动物价值 - 更重要
  for (const animal of gameState.capturedAnimals[player === 'black' ? 'white' : 'black']) {
    score += getAnimalValue(animal) * 1.2; // 被吃掉的动物价值更高
  }

  for (const animal of gameState.capturedAnimals[player]) {
    score -= getAnimalValue(animal) * 1.5; // 损失动物惩罚更大
  }

  // 移动性评估 - 更重要
  const playerMoves = getAllPossibleMoves(gameState, player);
  const enemyMoves = getAllPossibleMoves(gameState, player === 'black' ? 'white' : 'black');
  score += (playerMoves.length - enemyMoves.length) * EVALUATION_WEIGHTS.mobility;

  // 特殊策略评估
  // 检查是否有动物接近胜利
  const enemyDen = player === 'black' ? gameState.dens.white : gameState.dens.black;
  for (const animal of gameState.animals[player]) {
    const position = findAnimalPosition(gameState, animal);
    if (position) {
      const denDistance = Math.abs(position.col - enemyDen.col) + Math.abs(position.row - enemyDen.row);
      if (denDistance <= 2) {
        score += 50 * (3 - denDistance); // 接近胜利的巨大奖励
      }
    }
  }

  // 防守评估：保护巢穴
  const myDen = player === 'black' ? gameState.dens.black : gameState.dens.white;
  const animalNearMyDen = gameState.board[myDen.row][myDen.col];
  if (animalNearMyDen && animalNearMyDen.player !== player) {
    score -= 100; // 巢穴被占领的巨大惩罚
  }

  return score;
};

// 查找动物在棋盘上的位置
const findAnimalPosition = (gameState: JungleGameState, targetAnimal: JungleAnimal): Position | null => {
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 7; col++) {
      const animal = gameState.board[row][col];
      if (animal && animal.type === targetAnimal.type && animal.player === targetAnimal.player) {
        return { row, col };
      }
    }
  }
  return null;
};

// 最小最大搜索
const minimax = (
  gameState: JungleGameState,
  depth: number,
  alpha: number,
  beta: number,
  maximizingPlayer: boolean,
  player: Player
): { score: number; move?: JungleMove } => {
  // 检查游戏结束
  const gameEnd = checkGameEnd(gameState);
  if (gameEnd.isEnd || depth === 0) {
    const score = evaluatePosition(gameState, player);
    return { score };
  }

  const currentPlayer = gameState.currentPlayer;
  const moves = getAllPossibleMoves(gameState, currentPlayer);

  if (moves.length === 0) {
    const score = evaluatePosition(gameState, player);
    return { score };
  }

  let bestMove: JungleMove | undefined;
  let bestScore: number;

  if (maximizingPlayer) {
    bestScore = -Infinity;

    for (const move of moves) {
      const newGameState = makeMove(gameState, move);
      const result = minimax(newGameState, depth - 1, alpha, beta, false, player);

      if (result.score > bestScore) {
        bestScore = result.score;
        bestMove = move;
      }

      alpha = Math.max(alpha, bestScore);
      if (beta <= alpha) {
        break; // Alpha-beta剪枝
      }
    }
  } else {
    bestScore = Infinity;

    for (const move of moves) {
      const newGameState = makeMove(gameState, move);
      const result = minimax(newGameState, depth - 1, alpha, beta, true, player);

      if (result.score < bestScore) {
        bestScore = result.score;
        bestMove = move;
      }

      beta = Math.min(beta, bestScore);
      if (beta <= alpha) {
        break; // Alpha-beta剪枝
      }
    }
  }

  return { score: bestScore, move: bestMove };
};

// 辅助函数：执行移动
const makeMove = (gameState: JungleGameState, move: JungleMove): JungleGameState => {
  // 这里应该导入并使用 jungle.ts 中的 executeMove 函数
  // 为了避免循环依赖，我们简化实现
  const newBoard = gameState.board.map(row => [...row]);
  const animal = newBoard[move.from.row][move.from.col];

  if (animal) {
    newBoard[move.to.row][move.to.col] = animal;
    newBoard[move.from.row][move.from.col] = null;
  }

  const newAnimals = {
    black: [...gameState.animals.black],
    white: [...gameState.animals.white]
  };

  // 处理吃子
  if (move.captures && move.captures.length > 0) {
    const capturedAnimal = move.captures[0];
    const opponentPlayer = capturedAnimal.player;
    newAnimals[opponentPlayer] = newAnimals[opponentPlayer].filter(a =>
      !(a.type === capturedAnimal.type && a.player === capturedAnimal.player)
    );
  }

  return {
    ...gameState,
    board: newBoard,
    animals: newAnimals,
    currentPlayer: gameState.currentPlayer === 'black' ? 'white' : 'black',
    moves: [...gameState.moves, move],
    totalMoves: gameState.totalMoves + 1,
    captureMoves: gameState.captureMoves + (move.captures?.length || 0),
  };
};

// 根据难度获取搜索深度 - 加强难度
const getSearchDepth = (difficulty: Difficulty): number => {
  switch (difficulty) {
    case 'easy': return 2;   // 简单模式也提高一点
    case 'medium': return 4; // 中等模式大幅提高
    case 'hard': return 5;   // 困难模式更有挑战性
    default: return 3;
  }
};

// 主要AI决策函数 - 全面加强
export const getJungleAIDecision = (
  gameState: JungleGameState,
  difficulty: Difficulty = 'medium'
): JungleMove | null => {
  const moves = getAllPossibleMoves(gameState, gameState.currentPlayer);

  if (moves.length === 0) {
    return null;
  }

  // 简单模式：改进策略，不再是纯随机
  if (difficulty === 'easy') {
    return getEasyMove(gameState, moves);
  }

  // 中等和困难模式：使用minimax算法
  const depth = getSearchDepth(difficulty);
  const result = minimax(gameState, depth, -Infinity, Infinity, true, gameState.currentPlayer);

  return result.move || getBestBackupMove(gameState, moves);
};

// 简单模式的改进策略
const getEasyMove = (gameState: JungleGameState, moves: JungleMove[]): JungleMove => {
  // 优先级排序
  const winningMoves = moves.filter(move => isWinningMove(gameState, move));
  if (winningMoves.length > 0) {
    return winningMoves[0];
  }

  const captureMoves = moves.filter(move => move.captures && move.captures.length > 0);
  const denMoves = moves.filter(move => isApproachingDen(gameState, move));
  const riverJumps = moves.filter(move => move.isRiverJump);

  // 按优先级选择
  if (captureMoves.length > 0) {
    return captureMoves[Math.floor(Math.random() * captureMoves.length)];
  }
  if (denMoves.length > 0) {
    return denMoves[Math.floor(Math.random() * denMoves.length)];
  }
  if (riverJumps.length > 0) {
    return riverJumps[Math.floor(Math.random() * riverJumps.length)];
  }

  return moves[Math.floor(Math.random() * moves.length)];
};

// 检查是否是获胜移动
const isWinningMove = (gameState: JungleGameState, move: JungleMove): boolean => {
  const enemyDen = gameState.currentPlayer === 'black' ? gameState.dens.white : gameState.dens.black;
  return move.to.row === enemyDen.row && move.to.col === enemyDen.col;
};

// 检查是否接近巢穴
const isApproachingDen = (gameState: JungleGameState, move: JungleMove): boolean => {
  const enemyDen = gameState.currentPlayer === 'black' ? gameState.dens.white : gameState.dens.black;
  const currentDistance = Math.abs(move.from.col - enemyDen.col) + Math.abs(move.from.row - enemyDen.row);
  const newDistance = Math.abs(move.to.col - enemyDen.col) + Math.abs(move.to.row - enemyDen.row);
  return newDistance < currentDistance;
};

// 备用最佳移动选择
const getBestBackupMove = (gameState: JungleGameState, moves: JungleMove[]): JungleMove => {
  // 如果minimax失败，使用简单评估选择最佳移动
  let bestMove = moves[0];
  let bestScore = -Infinity;

  for (const move of moves) {
    let score = 0;

    if (move.captures && move.captures.length > 0) {
      score += getAnimalValue(move.captures[0]) * 2;
    }

    if (isWinningMove(gameState, move)) {
      score += 1000;
    }

    if (isApproachingDen(gameState, move)) {
      score += 20;
    }

    if (move.isRiverJump) {
      score += 15;
    }

    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }

  return bestMove;
};

// AI思考模拟 - 调整思考时间
export const simulateJungleAIThinking = async (difficulty: Difficulty = 'medium'): Promise<void> => {
  // 根据难度调整思考时间，让玩家感觉AI在认真思考
  const thinkingTime = difficulty === 'easy' ? 800 : difficulty === 'medium' ? 1200 : 1800;
  await new Promise(resolve => setTimeout(resolve, thinkingTime));
};

export default {
  getJungleAIDecision,
  simulateJungleAIThinking
};